import type { VariantProps } from 'class-variance-authority';

import * as React from 'react';
import { X } from 'lucide-react';
import { cva } from 'class-variance-authority';

import { cn } from '@shared/utils/cn';

const alertVariants = cva(
  'relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg border px-4 py-3 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        destructive:
          'border-destructive/40 bg-destructive/5 text-destructive [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90',
        success:
          'border-success/40 bg-success/5 text-success [&>svg]:text-current *:data-[slot=alert-description]:text-success/90',
        warning:
          'border-warning/40 bg-warning/5 text-warning [&>svg]:text-current *:data-[slot=alert-description]:text-warning/90',
      },
    },
    defaultVariants: { variant: 'default' },
  }
);

const Alert = ({
  className,
  variant,
  onClose,
  children,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants> & { onClose?: () => void }) => (
  <div
    className={cn(alertVariants({ variant }), onClose && 'pr-12', className)}
    data-slot="alert"
    role="alert"
    {...props}
  >
    {children}
    {onClose && (
      <button
        aria-label="Fermer"
        className="absolute top-2 right-2 flex size-8 items-center justify-center rounded-full text-current opacity-70 transition-opacity outline-none hover:opacity-100 focus-visible:ring-[3px] focus-visible:ring-ring/50"
        onClick={onClose}
        type="button"
      >
        <X className="size-4" />
      </button>
    )}
  </div>
);

const AlertTitle = ({ className, ...props }: React.ComponentProps<'div'>) => (
  <div
    className={cn('col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight', className)}
    data-slot="alert-title"
    {...props}
  />
);

const AlertDescription = ({ className, ...props }: React.ComponentProps<'div'>) => (
  <div
    className={cn(
      'col-start-2 grid justify-items-start gap-1 text-sm text-muted-foreground [&_p]:leading-relaxed',
      className
    )}
    data-slot="alert-description"
    {...props}
  />
);

export { Alert, AlertTitle, AlertDescription };
